import { Directionality } from '@angular/cdk/bidi';
import { HttpClient } from '@angular/common/http';
import { DestroyRef, DOCUMENT, inject, Service, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { EMPTY, Observable } from 'rxjs';
import { catchError, filter, first, map } from 'rxjs/operators';

import { WINDOW } from '@ideals/types';

import { IIntercomSettings, IntercomSettings } from './models';

const STYLE_POLL_INTERVAL_MS = 100;

export interface IntercomParams {
  readonly alignment?: 'right' | 'left';
}

type IntercomFn = (command: string, ...args: unknown[]) => void;

declare global {
  interface Window {
    readonly Intercom?: IntercomFn;
    // eslint-disable-next-line custom-rules/prefer-interface-property-readonly -- reassigned by loadIntercom / mutated by updateIntercom
    intercomSettings?: Record<string, unknown>;
  }
}

@Service()
export class IntercomService {
  readonly #destroyRef = inject(DestroyRef);
  readonly #directionality = inject(Directionality);
  readonly #document = inject(DOCUMENT);
  readonly #http = inject(HttpClient);
  readonly #window = inject<Window>(WINDOW);

  readonly #intercomLoaded = signal(false);
  readonly #intercomLoaded$ = toObservable(this.#intercomLoaded);

  #scriptLoaded = false;

  get intercomChat(): IntercomFn {
    return this.#window.Intercom;
  }

  loadIntercom(): void {
    if (this.#scriptLoaded) {
      return;
    }

    this.#scriptLoaded = true;

    this.#getIntercomSettings().subscribe({
      next: (intercomSettings) => {
        if (!intercomSettings.enabled) {
          return;
        }

        const script = this.#document.createElement('script');

        script.type = 'text/javascript';
        script.async = true;
        script.src = `https://widget.intercom.io/widget/${intercomSettings.app_id}`;
        script.onerror = () => this.#onIntercomError();
        script.onload = () => this.#onIntercomLoaded();
        this.#document.body.appendChild(script);
        this.#window.intercomSettings = {
          ...intercomSettings,
          alignment: this.#getAlignment(),
        };

        this.#subscribeToDirectionalityChanges();
      },
      error: () => {
        this.#onIntercomError();
      },
    });
  }

  showChat(): void {
    if (this.intercomChat && this.#scriptLoaded) {
      this.intercomChat('show');
    }
  }

  updateIntercom(params: IntercomParams): void {
    this.#intercomLoaded$
      .pipe(
        filter(Boolean),
        first(),
      )
      .subscribe(() => {
        Object.assign(this.#window.intercomSettings, params);
        this.#updateIntercom();
      });
  }

  #getAlignment(): 'right' | 'left' {
    return this.#directionality.value === 'rtl' ? 'left' : 'right';
  }

  #getIntercomSettings(): Observable<IntercomSettings> {
    return this.#http.get<IIntercomSettings>('/api/ui-settings/intercom')
      .pipe(
        map((data) => new IntercomSettings(data)),
        catchError(() => EMPTY),
      );
  }

  // TODO: Need add api for log error
  #onIntercomError(): void {}

  #onIntercomLoaded(): void {
    this.#intercomLoaded.set(true);

    const intercomStyles = `<style>
      .intercom-1o4wo1x, .e1tiptuf3 {font-size: 0;}
      .intercom-1o4wo1x:after, .e1tiptuf3:after {content: "30 sec.";font-size: 14px;}
      </style>`;

    this.intercomChat?.('onShow', () => {
      const interval = setInterval(() => {
        const iframe = this.#document.querySelector('.intercom-messenger-frame iframe') as HTMLIFrameElement;
        const loaded = iframe?.contentDocument?.querySelector('.intercom-messenger');

        if (loaded) {
          iframe.contentDocument.head.insertAdjacentHTML('beforeend', intercomStyles);
          clearInterval(interval);
        }
      }, STYLE_POLL_INTERVAL_MS);
    });
  }

  #subscribeToDirectionalityChanges(): void {
    this.#directionality.change
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.updateIntercom({ alignment: this.#getAlignment() });
      });
  }

  #updateIntercom(): void {
    this.intercomChat('update');
  }
}
